import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, message, Modal, Drawer, Button, Popconfirm } from 'antd';
import posthog from 'posthog-js';
import { InvoiceFormBase, createSupplierInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import InvoiceUploadandParse, {
	InvoiceLoadingSkeletonAnimated,
} from '../invoice_uploader/InvoiceUploadandParse';
import { adaptParsedInvoiceToFormData } from '../../utils/InvoiceDataAdapter';
import {
	invoicesRestCrudThunksForSupplier,
	updateInvoiceSubContractor,
	createInvoiceSubContractor,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
} from '../../thunks/invoices_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { getLinkWIthBackLinkParams, getLocationSearch } from '../../utils/HistoryUtils';
import {
	compareParsedWithFinal,
	buildInvoicePayloadFromFormValues,
} from '../../utils/InvoiceUtils';
import {
	getCurrency,
	getEntityById,
	isSubContractor,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import { isInternalTech } from '../../utils/AuthUtils';
import { getContractorWO } from '../../utils/WorkOrderUtils';
import { proposalsRestCrudThunksForSupplier } from '../../thunks/proposals_thunks';

const queryString = require('qs');
const { Content } = Layout;

const NewSupplierInvoicePageV2: FC<any> = ({
	createInvoice,
	updateInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	userType,
	currentUser,
	workOrder,
	creating,
	updating,
	createErrors,
	updateErrors,
	createContractorInvoice,
	updateContractorInvoice,
	isClientView,
	getQuotes,
}): React.ReactElement => {
	const [invoiceFormData, setInvoiceFormData] = useState<any>({});
	const [invoiceFormDataLoading, setInvoiceFormDataLoading] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	const [parsedDiscount, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const [hasQuote, setHasQuote] = useState(false);
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);
	const workOrderId = useMemo(() => {
		const searchString = getLocationSearch(location);
		const queryParams = queryString.parse(searchString);
		return queryParams.workOrderId || match.params.id;
	}, [location, match.params.id]);

	useEffect(() => {
		if (nullSafeGet('id', workOrder)) {
			getQuotes(
				{
					workOrderId: nullSafeGet('id', workOrder),
					buyerFacilityId: workOrder.buyerFacilityId,
				},
				'workOrderAssociatedQuotes'
			).then((proposals) => {
				if (proposals && proposals.length > 0) {
					setHasQuote(true);
				}
			});
		}
	}, []);
	const currency = useMemo(() => {
		if (hasQuote) {
			return getCurrency({ workOrder });
		}
		return getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
	}, [hasQuote, workOrder]);
	useEffect(() => {
		if (workOrderId) {
			setInvoiceFormData((prevFormData) => {
				if (prevFormData.workOrderId !== workOrderId) {
					return {
						...prevFormData,
						workOrderId: workOrderId,

						externalWorkOrderId: prevFormData.externalWorkOrderId || String(workOrderId),
						supplierFacilityId:
							workOrder && workOrder.supplierFacilityId
								? workOrder.supplierFacilityId
								: prevFormData.supplierFacilityId,
					};
				}

				if (!prevFormData.externalWorkOrderId) {
					return {
						...prevFormData,
						externalWorkOrderId: String(workOrderId),
					};
				}
				return prevFormData;
			});
		}
	}, [workOrderId, workOrder]);

	const handleDeletePdf = useCallback(() => {
		if (parsedFormDataSnapshot) {
			posthog.capture('invoice_pdf_deleted_after_parsing', {
				user_type: userType,
			});
		}
		setPdfUrl(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setFormKey((prev) => prev + 1);
		setShowDrawer(false);

		setInvoiceFormData((prevFormData) => {
			if (prevFormData) {
				return { ...prevFormData, invoicePDFLink: undefined };
			}
			return prevFormData;
		});
	}, [parsedFormDataSnapshot, userType]);

	const handleBackConfirm = useCallback(() => {
		handleDeletePdf();
	}, [handleDeletePdf]);

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);

		// Reset form data to initial state with only workOrderId
		setInvoiceFormData({
			workOrderId: workOrderId,
			externalWorkOrderId: String(workOrderId),
			supplierFacilityId:
				workOrder && workOrder.supplierFacilityId ? workOrder.supplierFacilityId : undefined,
		});

		// Increment form key to force re-render
		setFormKey((prev) => prev + 1);

		// Track reset event
		posthog.capture('invoice_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [workOrderId, workOrder, userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		// Show PDF immediately when file is selected
		if (fileData.fileUrl) {
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		} else {
			console.error('handleFileSelected: fileUrl is undefined');
		}
	}, []);

	const handleUploadError = useCallback((error = {}) => {
		message.error('Error parsing invoice. Please try again.');
		setParsingLoading(false);
		setShowDrawer(false);
		setFormKey((prev) => prev + 1);
		posthog.capture('invoice_pdf_parse_error', {
			user_type: userType,
			error: error.message,
		});
	}, []);

	const handleParsingStart = useCallback(() => {
		setParsingLoading(true);
	}, []);

	const handleParsingComplete = useCallback(() => {
		setParsingLoading(false);
	}, []);

	const handleUploadSuccess = useCallback(
		(data) => {
			const parsedCurrency = nullSafeGet('parsedData.currency', data) || 'USD';
			const systemCurrency = ((currency && currency.id) || 'USD').toUpperCase();
			if (parsedCurrency && parsedCurrency.toUpperCase() !== systemCurrency) {
				setPdfUrl(null);
				setParsingLoading(false);
				setShowDrawer(false);
				setFormKey((prev) => prev + 1);
				Modal.confirm({
					title: 'Currency Mismatch',
					content: `The uploaded invoice is in ${parsedCurrency}, but your account is configured to use ${systemCurrency}.`,
					okText: 'Create from Scratch',
					cancelText: 'Change Company Currency',
					cancelButtonProps: {
						href: '/supplier/company/me',
					},
					onOk: () => {
						history.push(
							`/supplier/workOrders/detail/${workOrderId}/invoices/${
								isInternalTech ? 'newForSupplier' : 'new'
							}`
						);
					},
					okButtonProps: {
						onClick: () => {
							posthog.capture('invoice_pdf_currency_mismatch_create_from_scratch', {
								user_type: userType,
								parsed_currency: parsedCurrency,
								system_currency: systemCurrency,
							});
						},
					},
					onCancel: () => {
						posthog.capture('invoice_pdf_currency_mismatch_change_currency', {
							user_type: userType,
							parsed_currency: parsedCurrency,
							system_currency: systemCurrency,
						});
					},
					width: '50vw',
				});
				return;
			}

			if (data.parsedData && workOrder) {
				const adaptedFormData = adaptParsedInvoiceToFormData(data.parsedData, workOrder);
				if (adaptedFormData) {
					setInvoiceFormData((prevFormData) => {
						const mergedFormData = {
							...prevFormData,
							...adaptedFormData,
							id: prevFormData.id,
							status: prevFormData.status,
							supplierFacilityId: prevFormData.supplierFacilityId,
							buyerFacilityId: prevFormData.buyerFacilityId,
							invoicePDFLink: data.fileUrl,
						};

						setParsedFormDataSnapshot(adaptedFormData);
						setParsedDiscount(nullSafeGet('parsedData.discount', data));
						setDisableCosts(nullSafeGet('parsedData.discount', data) > 0);

						const discount = nullSafeGet('parsedData.discount', data);
						const hasDiscount = (discount || 0) > 0;
						posthog.capture('invoice_pdf_parsed', {
							user_type: userType,
							has_parsed_data: true,
							has_discount: hasDiscount,
							...(hasDiscount && discount != null && { discount }),
						});

						return mergedFormData;
					});
				} else {
					posthog.capture('invoice_pdf_parsed', {
						user_type: userType,
						has_parsed_data: false,
						error: 'adaptation_failed',
					});
				}
			} else {
				posthog.capture('invoice_pdf_parsed', {
					user_type: userType,
					has_parsed_data: false,
					parsed_line_items_count: 0,
					parsed_fields: [],
					error: 'no_parsed_data',
				});
			}
			// Only update pdfUrl if a new one is provided (don't clear it)
			if (data.fileUrl) {
				setPdfUrl(data.fileUrl);
				setShowDrawer(true);
			}
			setFormKey((prev) => prev + 1);
		},
		[workOrder, userType, currency, history, workOrderId]
	);

	const onSuccess = useCallback(
		(rec) => {
			history.goBack();
		},
		[history]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const isUpdate = values.id ? true : false;
			const submitAction = values.id ? updateInvoice : createInvoice;
			const submitContractorAction = values.id ? updateContractorInvoice : createContractorInvoice;
			const contractorWO = getContractorWO(workOrder, isClientView);
			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			const supplierFacilityId = isInternalTech(currentUser)
				? nullSafeGet('supplierFacilityId', workOrder)
				: values.supplierFacilityId || nullSafeGet('facility.id', currentUser);

			const invoice = buildInvoicePayloadFromFormValues(formValues, scope, {
				workOrderId,
				currentUser,
				role: 'supplier',
				baseMeta: {
					invoicePDFLink: invoiceFormData && invoiceFormData.invoicePDFLink,
					isDeleted: invoiceFormData && invoiceFormData.isDeleted,
					buyerFacilityId: formValues.buyerFacilityId || nullSafeGet('facility.id', currentUser),
					supplierFacilityId,
				},
				status: isInternalTech(currentUser) ? 'pending' : 'draft',
			});

			if (formValues.signatureName) {
				invoice.aiParseMismatchSignedByEmail = currentUser.email;
			}

			try {
				const finalInvoice = isUpdate ? { ...invoiceFormData, ...invoice } : invoice;

				if (parsedFormDataSnapshot) {
					const changeMetrics = compareParsedWithFinal(parsedFormDataSnapshot, finalInvoice);
					const hadEdits =
						nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
						nullSafeGet('total_line_items_changed', changeMetrics) > 0;

					if (isUpdate) {
						posthog.capture('invoice_updated_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					} else {
						posthog.capture('invoice_created_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					}
					finalInvoice.isAiParsed = true;
					finalInvoice.isAiParsedEdited = hadEdits;
				}
				if (contractorWO && isSubContractor(currentUser, workOrder)) {
					const record = await submitContractorAction({
						...finalInvoice,
						workOrderId: undefined,
						supplierFacilityId: undefined,
						contractorWorkOrderId: contractorWO.id,
						contractorSupplierFacilityId: contractorWO.contractorSupplierFacilityId,
						subSupplierFacilityId: contractorWO.subSupplierFacilityId,
					});
					if (onSuccess) {
						onSuccess(record);
					}
				} else {
					const record = await submitAction(finalInvoice);
					if (onSuccess) {
						onSuccess(record);
					}
				}
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			updateInvoice,
			createInvoice,
			invoiceFormData,
			currentUser,
			onSuccess,
			history,
			parsedFormDataSnapshot,
			userType,
			workOrderId,
			updateContractorInvoice,
			createContractorInvoice,
			isClientView,
			workOrder,
		]
	);

	const formConfig = useMemo(() => {
		if (!invoiceFormData || !workOrder || !currentUser) {
			return null;
		}

		return createSupplierInvoiceConfig(
			invoiceFormData,
			workOrder,
			currentUser,
			history,
			handleSubmit,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				parsedDataSnapshot: parsedFormDataSnapshot,
				disableCosts: disableCosts,
				onReset: handleReset,
				fetchTaxLineItems,
				currency,
				checkInvoiceNumber,
				isClientView,
			}
		);
	}, [
		invoiceFormData,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		disableCosts,
		handleReset,
		fetchTaxLineItems,
		currency,
		checkInvoiceNumber,
		isClientView,
	]);

	if (invoiceFormDataLoading || !formConfig) {
		return <PageLoadingPlaceholder />;
	}

	return (
		<Content>
			<InvoiceUploadandParse
				key={formKey}
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={handleParsingStart}
				onParsingComplete={handleParsingComplete}
				uploaderType="dragger"
				onError={handleUploadError}
			/>
			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				// headerStyle={{ display: 'none' }}
				closeIcon={
					<Popconfirm
						title="Are you sure you want to go back? Any unsaved changes will be lost."
						onConfirm={handleBackConfirm}
						okText="Yes"
						cancelText="No"
					>
						<Button type="ghost" icon={<ArrowLeftOutlined />}>
							Back
						</Button>
					</Popconfirm>
				}
				title={<div className="h3medium">Create Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfUrl || null}
										fileName="Invoice.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							}
							rightPanel={
								<div>
									{parsingLoading ? (
										<InvoiceLoadingSkeletonAnimated />
									) : (
										<InvoiceFormBase
											key={formKey}
											config={formConfig}
											handleCancel={handleDeletePdf}
										/>
									)}
								</div>
							}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
	isClientView: state.clientView.isClientView,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	checkInvoiceNumber: (invoice, supplierFacilityId) =>
		dispatch(getInvoiceNumber(invoice, supplierFacilityId)),
	createInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.create(invoice)),
	updateInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.update(invoice)),
	fetchTaxLineItems: (currencyId) => dispatch(fetchTaxLineItemsForSupplier(currencyId)),
	createContractorInvoice: (invoice) => dispatch(createInvoiceSubContractor(invoice)),
	updateContractorInvoice: (invoice) => dispatch(updateInvoiceSubContractor(invoice)),
	getQuotes: (params, targetCollectionName) =>
		dispatch(proposalsRestCrudThunksForSupplier.read(params, targetCollectionName)),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(NewSupplierInvoicePageV2))
);
